import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useToast } from '../ui/toast';
import { LibraryItemPicker } from './LibraryItemPicker';
import { createTemplate, readFileAsBase64 } from '../../data/templates';
import { SLIDE_LIBRARY_COUNTRIES, SLIDE_LIBRARY_LANGUAGES } from '../../data/slideLibrary';
import type { CategorySummary, LibraryItem, TemplateSource } from '../../data/types';

const ACCEPTED = '.docx,.xlsx,.pptx';

interface TemplateUploadFormProps {
  categories: CategorySummary[];
}

export function TemplateUploadForm({ categories }: TemplateUploadFormProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [country, setCountry] = useState('');
  const [language, setLanguage] = useState('');
  const [source, setSource] = useState<TemplateSource>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [libraryItem, setLibraryItem] = useState<LibraryItem | null>(null);
  const [busy, setBusy] = useState(false);

  const ready = (source === 'upload' ? file !== null : libraryItem !== null) && categoryId !== '' && country !== '' && language !== '';

  const chooseLibraryItem = (item: LibraryItem | null) => {
    setLibraryItem(item);
    // The library file name is the best default the author has; typing over it still wins.
    if (item && name.trim().length === 0) {
      setName(item.name.replace(/\.(docx|xlsx|pptx)$/i, ''));
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!ready || busy) return;

    setBusy(true);
    try {
      const detail = await createTemplate({
        name: name.trim(),
        description: description.trim(),
        categoryId: categoryId || undefined,
        country,
        language,
        ...(source === 'library'
          ? { libraryItemId: libraryItem!.id }
          : { fileName: file!.name, fileContentBase64: await readFileAsBase64(file!) })
      });

      toast({
        title: t('studio.upload.success', { count: detail.properties.length }),
        type: 'success'
      });

      // The discovered tokens still need types, defaults and publishing, so the
      // template itself is the next stop — not the list it was uploaded from.
      void navigate(`/studio/templates/${detail.template.id}`);
    } catch (error) {
      toast({
        title: error instanceof Error ? error.message : t('errors.uploadTemplate'),
        type: 'error'
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-6 rounded-lg border border-border bg-surface p-4">
      <h2 className="text-base font-semibold text-foreground">{t('studio.upload.title')}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{t('studio.upload.hint')}</p>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="font-medium text-foreground">{t('studio.upload.name')}</span>
          <input
            type="text"
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="mt-1 w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22"
          />
        </label>

        <label className="block text-sm">
          <span className="font-medium text-foreground">{t('studio.upload.category')}</span>
          <select
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value)}
            required
            className="mt-1 w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22"
          >
            <option value="" disabled>
              {t('studio.upload.chooseCategory')}
            </option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm">
          <span className="font-medium text-foreground">{t('studio.upload.country')}</span>
          <select
            value={country}
            onChange={(event) => setCountry(event.target.value)}
            required
            className="mt-1 w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22"
          >
            <option value="" disabled>
              {t('studio.upload.chooseCountry')}
            </option>
            {SLIDE_LIBRARY_COUNTRIES.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm">
          <span className="font-medium text-foreground">{t('studio.upload.language')}</span>
          <select
            value={language}
            onChange={(event) => setLanguage(event.target.value)}
            required
            className="mt-1 w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22"
          >
            <option value="" disabled>
              {t('studio.upload.chooseLanguage')}
            </option>
            {SLIDE_LIBRARY_LANGUAGES.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm sm:col-span-2">
          <span className="font-medium text-foreground">{t('studio.upload.description')}</span>
          <input
            type="text"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            className="mt-1 w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22"
          />
        </label>

        <fieldset className="block text-sm sm:col-span-2">
          <legend className="font-medium text-foreground">{t('studio.upload.source')}</legend>
          <div className="mt-1 flex flex-wrap gap-4">
            {(['upload', 'library'] as const).map((option) => (
              <label key={option} className="inline-flex cursor-pointer items-center gap-2">
                <input
                  type="radio"
                  name="template-source"
                  value={option}
                  checked={source === option}
                  onChange={() => setSource(option)}
                  className="size-4 accent-primary"
                />
                <span className="text-foreground">{t(`studio.upload.source_${option}`)}</span>
              </label>
            ))}
          </div>
        </fieldset>

        {source === 'upload' ? (
          <label className="block text-sm sm:col-span-2">
            <span className="font-medium text-foreground">{t('studio.upload.file')}</span>
            <input
              type="file"
              required
              accept={ACCEPTED}
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              className="mt-1 w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm file:mr-3 file:rounded file:border-0 file:bg-muted file:px-3 file:py-1 file:text-sm file:font-semibold file:text-foreground"
            />
          </label>
        ) : (
          <div className="sm:col-span-2">
            <LibraryItemPicker idPrefix="template-create" value={libraryItem?.id ?? ''} onChange={chooseLibraryItem} libraryType="Templates" />
          </div>
        )}
      </div>

      <button
        type="submit"
        disabled={busy || !ready}
        className="mt-4 inline-flex items-center justify-center gap-2 rounded bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground ring-1 ring-primary-hover transition hover:bg-primary-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-60"
      >
        <ArrowUpTrayIcon className="size-4" aria-hidden="true" />
        {busy ? t('studio.upload.uploading') : t('studio.upload.submit')}
      </button>
    </form>
  );
}
